import { api } from '../client';
import type {
  Assignment,
  AssignmentKind,
  AssignmentType,
  ClassTeacher,
  ClassTeacherRole,
  DayOfWeek,
  Submission,
  TeacherAssignment,
  Timetable,
  TimetableEntry,
  UUID,
} from '@/types/domain';

export interface TeacherAssignmentPayload {
  teacherId: UUID;
  academicSubjectId: UUID;
  sectionId: UUID;
  assignmentType: AssignmentType;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface EndAssignmentPayload {
  effectiveTo: string;
  reason?: string;
}

export interface ClassTeacherPayload {
  teacherId: UUID;
  sectionId: UUID;
  role: ClassTeacherRole;
  effectiveFrom: string;
  effectiveTo?: string;
}

export const teacherAssignmentsApi = {
  /** Supply exactly one of teacherId or sectionId. */
  list: (params: { teacherId?: UUID; sectionId?: UUID }) =>
    api.get<TeacherAssignment[]>('/teacher-assignments', { params }),
  get: (id: UUID) => api.get<TeacherAssignment>(`/teacher-assignments/${id}`),
  create: (payload: TeacherAssignmentPayload) =>
    api.post<TeacherAssignment>('/teacher-assignments', payload),
  /** Dates the assignment out and keeps the row, so the lessons under it stay intact. */
  end: (id: UUID, payload: EndAssignmentPayload) =>
    api.put<TeacherAssignment>(`/teacher-assignments/${id}/end`, payload),

  classTeachers: (sectionId: UUID) =>
    api.get<ClassTeacher[]>('/teacher-assignments/class-teachers', { params: { sectionId } }),
  assignClassTeacher: (payload: ClassTeacherPayload) =>
    api.post<ClassTeacher>('/teacher-assignments/class-teachers', payload),
  endClassTeacher: (id: UUID, payload: EndAssignmentPayload) =>
    api.put<ClassTeacher>(`/teacher-assignments/class-teachers/${id}/end`, payload),
};

export interface TimetablePayload {
  sectionId: UUID;
  name?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface TimetableEntryPayload {
  dayOfWeek: DayOfWeek;
  periodId: UUID;
  /** The teacher comes from the assignment, so the two cannot disagree. */
  teacherAssignmentId: UUID;
  room?: string;
}

export const timetablesApi = {
  list: (sectionId: UUID) => api.get<Timetable[]>('/timetables', { params: { sectionId } }),
  /** The schedule a section is actually following. */
  published: (sectionId: UUID) =>
    api.get<Timetable>('/timetables/published', { params: { sectionId } }),
  get: (id: UUID) => api.get<Timetable>(`/timetables/${id}`),
  /** The signed-in teacher's published week, resolved from the account. Empty for a non-teacher. */
  mine: () => api.get<TimetableEntry[]>('/timetables/me'),
  /** One teacher's week, across every section they teach. */
  forTeacher: (teacherId: UUID) => api.get<TimetableEntry[]>(`/timetables/by-teacher/${teacherId}`),
  create: (payload: TimetablePayload) => api.post<Timetable>('/timetables', payload),
  addEntry: (id: UUID, payload: TimetableEntryPayload) =>
    api.post<Timetable>(`/timetables/${id}/entries`, payload),
  removeEntry: (id: UUID, entryId: UUID) =>
    api.delete<Timetable>(`/timetables/${id}/entries/${entryId}`),
  /** Archives the outgoing timetable rather than replacing it. */
  publish: (id: UUID) => api.post<Timetable>(`/timetables/${id}/publish`),
};

export interface AssignmentPayload {
  teacherAssignmentId: UUID;
  title: string;
  description?: string;
  assignmentType: AssignmentKind;
  assignedDate: string;
  dueDate: string;
  maxMarks?: number;
  allowLateSubmission: boolean;
}

export const assignmentsApi = {
  list: (teacherAssignmentId: UUID, includeDrafts = false) =>
    api.get<Assignment[]>('/assignments', { params: { teacherAssignmentId, includeDrafts } }),
  get: (id: UUID) => api.get<Assignment>(`/assignments/${id}`),
  create: (payload: AssignmentPayload) => api.post<Assignment>('/assignments', payload),
  update: (id: UUID, payload: AssignmentPayload) => api.put<Assignment>(`/assignments/${id}`, payload),
  /** Creates a not-submitted entry for every child in the class as it stands today. */
  publish: (id: UUID) => api.post<Assignment>(`/assignments/${id}/publish`),
  close: (id: UUID) => api.post<Assignment>(`/assignments/${id}/close`),

  submissions: (id: UUID) => api.get<Submission[]>(`/assignments/${id}/submissions`),
  submit: (id: UUID, payload: { enrollmentId: UUID; submissionText?: string }) =>
    api.post<Submission>(`/assignments/${id}/submissions`, payload),
  /** Comments only — marks belong to the Examination module. */
  giveFeedback: (id: UUID, submissionId: UUID, feedback: string) =>
    api.put<Submission>(`/assignments/${id}/submissions/${submissionId}/feedback`, { feedback }),
};
